import {
  ReportedInvoiceItemFormInterface,
  ReportedInvoiceItemInterface,
} from "@/modules/invoices/types";

const MAJOR_AMOUNT_PATTERN = /^\d+(?:[.,]\d{0,2})?$/;

export const parseMajorAmountToCents = (
  amount: string | number | null | undefined
): number | null => {
  if (amount === null || amount === undefined) {
    return null;
  }

  const normalizedInput = String(amount).trim().replace(",", ".");
  if (!normalizedInput || !MAJOR_AMOUNT_PATTERN.test(normalizedInput)) {
    return null;
  }

  const [majorUnits = "0", decimals = ""] = normalizedInput.split(".");
  const major = Number.parseInt(majorUnits, 10);
  if (Number.isNaN(major)) {
    return null;
  }

  const decimalPart = Number.parseInt(decimals.padEnd(2, "0"), 10) || 0;
  return major * 100 + decimalPart;
};

export const formatCentsToMajorAmount = (cents: number | null | undefined): string => {
  if (cents === null || cents === undefined || !Number.isInteger(cents)) {
    return "0.00";
  }

  const sign = cents < 0 ? "-" : "";
  const absolute = Math.abs(cents);
  const majorUnits = Math.floor(absolute / 100);
  const decimalPart = String(absolute % 100).padStart(2, "0");
  return `${sign}${majorUnits}.${decimalPart}`;
};

export const formatCentsWithCurrency = (cents: number, currency?: string | null): string => {
  if (!currency) {
    return "-";
  }
  return `${formatCentsToMajorAmount(cents)} ${currency}`;
};

export const sanitizeReportedAmountInput = (value: string | undefined): string => {
  const normalizedInput = (value || "").replace(",", ".");
  const sanitized = normalizedInput.replace(/[^0-9.]/g, "");
  if (!sanitized) {
    return "";
  }

  const [integerPart = "", ...decimals] = sanitized.split(".");
  const nextIntegerPart = integerPart || "0";
  const nextDecimalPart = decimals.join("").slice(0, 2);
  return nextDecimalPart.length ? `${nextIntegerPart}.${nextDecimalPart}` : nextIntegerPart;
};

export const normalizeReportedAmountInputValue = (
  value: string | number | null | undefined
): string => {
  if (value === null || value === undefined) {
    return "";
  }
  return sanitizeReportedAmountInput(String(value));
};

export const normalizeReportedItemForForm = (
  item: any,
  defaultCurrency: string = "SEK"
): ReportedInvoiceItemFormInterface => {
  const fallbackAmountCents = (Number(item.unit_cost_cents) || 0) * (Number(item.quantity) || 0);
  const amountCents = Number.isInteger(item.amount_cents) ? item.amount_cents : fallbackAmountCents;
  const amountCurrency = item.amount_currency || item.unit_cost_currency || defaultCurrency;

  return {
    id: item.id,
    source_subproject_id: item.source_subproject_id,
    kind: item.kind,
    description: item.description || "",
    amount_major: formatCentsToMajorAmount(amountCents),
    amount_currency: amountCurrency,
  };
};

export const reportedRowsTotalCents = (rows: Array<ReportedInvoiceItemFormInterface>): number => {
  return rows.reduce((sum, row) => {
    const rowCents = parseMajorAmountToCents(row.amount_major);
    return sum + (rowCents ?? 0);
  }, 0);
};

export const reportedRowsDiffCents = (
  reportsTotalCents: number,
  rowsTotalCents: number
): number => {
  return reportsTotalCents - rowsTotalCents;
};

export const validateReportedRows = ({
  rows,
  selectedReportsTotalCents,
  selectedReportsCurrency,
}: {
  rows: Array<ReportedInvoiceItemFormInterface>;
  selectedReportsTotalCents: number;
  selectedReportsCurrency: string | null;
}): {
  hasRows: boolean;
  hasInvalidAmount: boolean;
  hasCurrencyMismatch: boolean;
  hasTotalMismatch: boolean;
  isValid: boolean;
} => {
  const hasRows = rows.length > 0;
  const hasInvalidAmount = rows.some((row) => parseMajorAmountToCents(row.amount_major) === null);
  const rowCurrency = rows[0]?.amount_currency || null;
  const hasCurrencyMismatch =
    !!selectedReportsCurrency && !!rowCurrency && rowCurrency !== selectedReportsCurrency;
  const hasTotalMismatch =
    hasRows && reportedRowsTotalCents(rows) !== Number(selectedReportsTotalCents || 0);
  const isValid = hasRows && !hasInvalidAmount && !hasCurrencyMismatch && !hasTotalMismatch;

  return {
    hasRows,
    hasInvalidAmount,
    hasCurrencyMismatch,
    hasTotalMismatch,
    isValid,
  };
};

export const normalizeReportedItemsForPayload = (
  rows: Array<ReportedInvoiceItemFormInterface>
): Array<ReportedInvoiceItemInterface> => {
  return rows.map((row) => ({
    id: row.id,
    source_subproject_id: row.source_subproject_id,
    kind: row.kind,
    description: row.description,
    amount_cents: parseMajorAmountToCents(row.amount_major) ?? 0,
    amount_currency: row.amount_currency,
  }));
};
